import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import {
  getIntersectionRoot,
  getScrollTop,
  hasPendingScrollAnchor,
  holdScrollPosition,
  restoreScrollAnchor,
  saveScrollAnchor,
  scrollContainerMediaQuery,
  scrollTo,
} from '@theme/scroll-container';

const MAX_RESTORED_PAGES = 4;

const CARD_SELECTOR = ':scope > [ref="cards[]"]';
const BANNER_SELECTOR = ':scope > .product-grid__item--in-grid-banner';

/**
 * @param {HTMLElement} grid
 * @returns {HTMLElement[]} Cards at least partly inside the viewport, in DOM order.
 */
function visibleCards(grid) {
  return /** @type {HTMLElement[]} */ (Array.from(grid.querySelectorAll(CARD_SELECTOR))).filter((card) => {
    const { top, bottom, height } = card.getBoundingClientRect();
    return height > 0 && bottom > 0 && top < window.innerHeight;
  });
}

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLElement} [viewMoreNext] - The sentinel that loads the next page as it nears the viewport.
 * @property {HTMLButtonElement} [loadMoreButton] - The Load More button, when the theme uses one instead.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** A list that first rendered with no results had no card to build the helper from. */
  #processNewElements() {
    if (!this.#aspectRatioHelper) {
      const templateCard = this.querySelector('[ref="cardGallery"]');
      if (templateCard instanceof HTMLElement) {
        this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
      }
    }

    this.#aspectRatioHelper?.processNewElements();
  }

  /**
   * Guards against overlapping appends. `#getPage` derives the next page from the last rendered
   * card, so it only advances once an append has actually happened — two renders started before
   * the first finishes therefore resolve to the same page and append it twice. The Load More
   * button disables itself while busy, but the infinite-scroll observer has no such interlock and
   * can fire again while a page is still in flight.
   * @type {boolean}
   */
  #renderingNext = false;

  /** @type {boolean} */
  #renderingPrevious = false;

  /**
   * Invalidates page fetches that are still in flight when the filters or sort change.
   * `#handleFilterUpdate` clears the cache, but a fetch already awaiting its response resolves
   * afterwards and writes pre-filter HTML back into the cleared cache. The recovery path only
   * re-fetches the page adjacent to the last rendered card, so a poisoned entry for any later
   * page survives and is appended verbatim once the shopper scrolls that far.
   * @type {number}
   */
  #requestToken = 0;

  /**
   * Filters out cards already rendered in the grid. Shopify can return the same product on two
   * pages when the sort order is non-deterministic or inventory shifts between requests, and the
   * guards above cannot help there because the duplicate arrives in a legitimately different page.
   * @param {HTMLElement} grid - The live grid element.
   * @param {Iterable<Element>} elements - Candidate cards to add.
   * @returns {Element[]} The cards not already on screen.
   */
  #withoutRenderedCards(grid, elements) {
    const rendered = new Set(
      Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'))
        .map((card) => /** @type {HTMLElement} */ (card).dataset.productId)
        .filter(Boolean)
    );

    const hasBanner = grid.querySelector(BANNER_SELECTOR) !== null;

    return Array.from(elements).filter((element) => {
      const { productId } = /** @type {HTMLElement} */ (element).dataset;
      if (!productId) return !(hasBanner && element.matches('.product-grid__item--in-grid-banner'));
      if (rendered.has(productId)) return false;
      rendered.add(productId);
      return true;
    });
  }

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#restorePreviousPages();
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    scrollContainerMediaQuery.addEventListener('change', this.#resetObserver);
    window.addEventListener('pagehide', this.#saveScrollAnchor);
    this.addEventListener('click', this.#rememberClickedCard, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    scrollContainerMediaQuery.removeEventListener('change', this.#resetObserver);
    window.removeEventListener('pagehide', this.#saveScrollAnchor);
    this.removeEventListener('click', this.#rememberClickedCard, { capture: true });
  }

  #resetObserver = () => {
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;
    this.#observeViewMore();
  };

  #observeViewMore() {
    // Queried, not `refs`: a filter morph out of zero results inserts new sentinels before refs catch up.
    const viewMorePrevious = this.querySelector('[ref="viewMorePrevious"]');
    const viewMoreNext = this.querySelector('[ref="viewMoreNext"]');

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting && entry.target.isConnected) {
              const ref = entry.target.getAttribute('ref');

              if (ref === 'viewMorePrevious') {
                this.#renderPreviousPage();
              } else if (ref === 'viewMoreNext') {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          // `.page-wrapper` scrolls at ≥1024, and a margin on the implicit root never extends past its clip.
          root: getIntersectionRoot(),
          rootMargin: '50% 0px 100% 0px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  #syncViewMoreNext() {
    const viewMoreNext = /** @type {HTMLElement | null} */ (this.querySelector('[ref="viewMoreNext"]'));
    if (!viewMoreNext) return;

    viewMoreNext.hidden = !this.#shouldUsePage(this.#getPage('next'));
    if (viewMoreNext.hidden || !this.infinityScrollObserver) return;

    // A fresh observation reports the current state, so a sentinel still inside the margin keeps loading.
    this.infinityScrollObserver.unobserve(viewMoreNext);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * Replaced, not pushed: loaded pages must not cost the shopper a Back press each.
   * @param {URL} url
   */
  #replaceURL(url) {
    try {
      history.replaceState(history.state, '', url.toString());
    } catch (_) {
      // replaceState can throw if the state object exceeds the browser's size limit
    }
  }

  /** @type {HTMLElement | null} */
  #clickedCard = null;

  /** @param {MouseEvent} event */
  #rememberClickedCard = (event) => {
    const card = event.target instanceof Element ? event.target.closest('[ref="cards[]"]') : null;
    this.#clickedCard = card instanceof HTMLElement ? card : null;
  };

  /**
   * Anchors on a card the server renders again on Back, moving the URL page to it if needed. The
   * clicked card is preferred: cards above it can still change height while their content loads.
   */
  #saveScrollAnchor = () => {
    const { grid } = this.refs;
    if (!grid || window.Shopify?.designMode) return;

    const url = new URL(window.location.href);
    const renderedPage = url.searchParams.get('page') ?? '1';
    const cards = visibleCards(grid).filter((card) => card.id && card.dataset.page);
    const card =
      cards.find((card) => card === this.#clickedCard) ??
      cards.find((card) => card.dataset.page === renderedPage) ??
      cards[0];

    // Chromium keeps this; WebKit drops it, and the anchor is found once the pages above are re-rendered.
    if (card?.dataset.page && card.dataset.page !== renderedPage) {
      url.searchParams.set('page', card.dataset.page);
      this.#replaceURL(url);
    }

    saveScrollAnchor(card ?? null);
  };

  /**
   * Back to `?page=3` renders only page 3. When the saved anchor sat on a page above it, re-render
   * pages until it is back; anything further up is left to the scroll-up sentinel.
   */
  async #restorePreviousPages() {
    if (!this.refs.viewMorePrevious || window.Shopify?.designMode || !hasPendingScrollAnchor()) return;

    this.#renderingPrevious = true;

    try {
      for (let i = 0; i < MAX_RESTORED_PAGES && hasPendingScrollAnchor(); i++) {
        if (i) await yieldToMainThread();
        if (!(await this.#prependPreviousPage())) break;
        restoreScrollAnchor();
      }
    } finally {
      this.#renderingPrevious = false;
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (_) {
      // Aborted by navigation or offline; the next render that needs this page fetches it again.
    } finally {
      // A failed fetch left the waiting render, and with it every later page, stuck behind this promise.
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const token = this.#requestToken;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);

    // The filters moved while this was in flight, so the response describes the old result set.
    if (token !== this.#requestToken) return;

    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#renderingNext) return;
    this.#renderingNext = true;

    /** @type {{ page: number, url: URL } | null} */
    let appended = null;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      const freshElements = this.#withoutRenderedCards(grid, nextPageItemElements);
      if (!freshElements.length) return;

      grid.append(...freshElements);
      appended = nextPage;
    } finally {
      // Released the moment the append lands. `#getPage` now reports the page after this one, so
      // a concurrent render can no longer resolve to the page just added — which is the whole job
      // of the guard. Everything below is presentation and prefetch, and holding the guard across
      // it would strand lazy loading entirely if one of those awaits never settled:
      // `yieldToMainThread` does not resolve while the tab is hidden.
      this.#renderingNext = false;
    }

    if (!appended) return;

    this.#processNewElements();

    await yieldToMainThread();

    this.#replaceURL(appended.url);
    this.#syncViewMoreNext();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    /** @type {{ page: number, url: URL } | null | undefined} */
    let prepended;

    if (this.#renderingPrevious) return;
    this.#renderingPrevious = true;

    try {
      prepended = await this.#prependPreviousPage();
    } finally {
      // See #renderNextPage: released as soon as the prepend and its scroll correction are done.
      this.#renderingPrevious = false;
    }

    if (!prepended) return;

    await yieldToMainThread();

    this.#replaceURL(prepended.url);

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Prepends the previous page and corrects the scroll offset so the viewport does not jump.
   * @returns {Promise<{ page: number, url: URL } | null>} The page prepended, or null if none was.
   */
  async #prependPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return null;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return null;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return null;
    }

    const freshElements = this.#withoutRenderedCards(grid, previousPageItemElements);
    if (!freshElements.length) return null;

    // Not `grid.firstElementChild`: that is the grid-view <script>, whose empty rect left WebKit uncorrected.
    const pin = visibleCards(grid)[0] ?? grid.querySelector(CARD_SELECTOR);
    const pinTop = pin?.getBoundingClientRect().top ?? 0;

    // Prepend the new elements
    grid.prepend(...freshElements);

    this.#processNewElements();

    // Relative to the scroll position after the insert, so Chromium's own anchoring is not doubled.
    if (pin) {
      scrollTo({
        top: getScrollTop() + pin.getBoundingClientRect().top - pinTop,
        behavior: 'instant',
      });
      holdScrollPosition(pin);
    }

    return previousPage;
  }

  /**
   * Public method triggered by the Load More button click.
   * Appends the next page of products to the grid.
   */
  async loadMore() {
    const loadMoreButton = this.#loadMoreButton;
    if (!loadMoreButton) return;

    // Show busy state
    loadMoreButton.setAttribute('aria-busy', 'true');
    loadMoreButton.disabled = true;

    await this.#renderNextPage();

    // Update button visibility after render
    this.#updateLoadMoreButton();
  }

  /** Queried like the sentinels: a morph out of zero results renders a new button. */
  get #loadMoreButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="loadMoreButton"]'));
  }

  /**
   * Checks if more pages remain and hides or resets the Load More button.
   */
  #updateLoadMoreButton() {
    const loadMoreButton = this.#loadMoreButton;
    if (!loadMoreButton) return;

    const nextPage = this.#getPage('next');
    const hasMorePages = nextPage && this.#shouldUsePage(nextPage);

    if (!hasMorePages) {
      // No more pages — hide the button container
      const container = loadMoreButton.closest('.load-more-container');

      if (container instanceof HTMLElement) {
        container.hidden = true;
      }
    } else {
      // Reset button state for the next click
      loadMoreButton.removeAttribute('aria-busy');
      loadMoreButton.disabled = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    // Not `refs.cards`: its observer attaches on idle, so pages loaded before that left it stuck on page 2.
    const cards = /** @type {HTMLElement[]} */ (Array.from(this.refs.grid?.querySelectorAll(CARD_SELECTOR) ?? []));
    const isPrevious = type === 'previous';

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    // The in-grid banner renders with page 1, and rebuilding page 1 on Back dropped it.
    return gridElement.querySelectorAll(`${CARD_SELECTOR}, ${BANNER_SELECTOR}`);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.#requestToken++;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const lastPage = () => /** @type {HTMLElement | null} */ (this.querySelector('[ref="grid"]'))?.dataset.lastPage;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = lastPage();

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = lastPage();

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#resetObserver();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // The component, not the grid: an empty result set renders no grid, and the next one is a new node.
    observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-last-page'],
      childList: true,
      subtree: true,
    });

    // Set a timeout as a fallback in case the mutation never fires
    setTimeout(() => observer.disconnect(), 3000);
  };
}
